// Viewport: canvas stack, camera, navigation input (RND-2), spatial index, and the
// delegate seams the tools layer plugs into (ARC-3a: engine cannot import tools).
import { type Vec } from "../core/geometry";
import { docBounds } from "../doc/derived";
import type { DocStore } from "../doc/store";
import { createCamera, fitBounds, panBy, screenToWorld, zoomAt, zoomPercent, type Camera } from "./camera";
import { RenderScheduler, RENDER_LAYERS, type RenderLayerId } from "./renderScheduler";
import { renderBg, type BgTheme } from "./renderBg";
import { renderDocEntities, type RenderView } from "./renderEntities";
import { renderImages } from "./renderImages";
import { AssetCache } from "./assetCache";
import { SpatialIndex } from "./spatialIndex";

export interface ViewportEvents {
  onCursor?: (world: Vec) => void;
  onCamera?: (cam: Camera, percent: number) => void;
}

export interface PointerMods {
  shift: boolean;
  ctrl: boolean;
  alt: boolean;
}

/** Implemented by the tool manager (tools layer) and injected at composition time. */
export interface ViewportDelegate {
  pointerDown(screen: Vec, mods: PointerMods, button: number): void;
  pointerMove(screen: Vec, mods: PointerMods): void;
  pointerUp(screen: Vec, mods: PointerMods, button: number): void;
  doubleClick(screen: Vec, mods: PointerMods): void;
  /** Return true when consumed. */
  key(e: { key: string; ctrl: boolean; shift: boolean; alt: boolean }): boolean;
  spaceTap?(): void; // TL-2: Space tap = repeat last draw tool
  contextMenu?(screen: Vec, mods: PointerMods): void; // TL-2: right-click menu (UI-4)
  /** 3.20.0: the wheel goes to the tool first (an image scales while it is dragged). True = used: no zoom, no pan. */
  wheel?(screen: Vec, deltaY: number, mods: PointerMods): boolean;
}

function readTheme(): BgTheme {
  const css = getComputedStyle(document.documentElement);
  const get = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback;
  return {
    canvas: get("--canvas", "#14161c"),
    gridMinor: get("--grid-minor", "#1d2029"),
    gridMajor: get("--grid-major", "#272b37"),
    axisX: get("--axis-x", "#e05c5c"),
    axisY: get("--axis-y", "#4caf7d")
  };
}

export { docBounds } from "../doc/derived"; // moved (pure derived math, ARC-3a)

export class Viewport {
  readonly camera: { current: Camera } = { current: createCamera() };
  readonly scheduler = new RenderScheduler();
  readonly events: ViewportEvents = {};
  readonly index = new SpatialIndex();
  readonly assets = new AssetCache();
  editingIds: ReadonlySet<string> = new Set();
  delegate: ViewportDelegate | null = null;
  /** IMG-12: images render at 40% while a draw tool is active (settings toggle). */
  dimImages = false;
  /** ANN-7 construction guides (infinite lines; viewState, never exported). Max 32. */
  guides: { origin: Vec; dir: Vec }[] = [];
  /** Set by the composition root to persist viewState when guides change. */
  onGuidesChanged: (() => void) | null = null;

  addGuide(origin: Vec, dir: Vec): void {
    const len = Math.hypot(dir.x, dir.y);
    if (len < 1e-9) return;
    this.guides.push({ origin, dir: { x: dir.x / len, y: dir.y / len } });
    if (this.guides.length > 32) this.guides.shift(); // EC-ANN-9
    this.scheduler.invalidate("overlay");
    this.onGuidesChanged?.();
  }

  clearGuides(): void {
    if (this.guides.length === 0) return;
    this.guides = [];
    this.scheduler.invalidate("overlay");
    this.onGuidesChanged?.();
  }
  /** TL-5a: interaction-modifier keys currently held (edge slide/move). */
  readonly heldKeys = new Set<string>();

  private container: HTMLElement;
  private store: DocStore;
  private topCanvas!: HTMLCanvasElement;
  private baseCursor = "crosshair"; // tool-owned cursor; pan overrides restore this
  private panPointer: number | null = null;
  private lastPointer: Vec = { x: 0, y: 0 };
  private spaceHeld = false;
  private spaceDownAt = 0;
  private pannedDuringSpace = false;
  private dynamicRenderer: ((v: RenderView) => void) | null = null;
  private overlayRenderer: ((v: RenderView) => void) | null = null;
  private detachFns: (() => void)[] = [];

  // RND-4: during camera gestures, blit the last full static/bg render with a delta
  // transform instead of re-rendering; crisp full render on 120 ms settle.
  private blitCache: {
    layers: Partial<Record<"bg" | "static", HTMLCanvasElement>>;
    cam: Camera;
    dpr: number;
  } | null = null;
  private renderedCam: Camera | null = null; // camera of the last FULL bg/static render
  private settleTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(container: HTMLElement, store: DocStore) {
    this.container = container;
    this.store = store;
    this.index.rebuild(store.doc);
    this.assets.onLoaded = () => this.scheduler.invalidate("bg");
    this.createCanvases();
    this.wireResize();
    this.wireInput();
    this.detachFns.push(
      store.subscribe((doc, cmd) => {
        if (cmd) this.index.applyCommand(doc, cmd);
        else this.index.rebuild(doc);
        this.blitCache = null; // doc changed → the cached render is stale (RND-4)
        this.scheduler.invalidate("bg", "static");
      })
    );
  }

  setDynamicRenderer(fn: (v: RenderView) => void): void {
    this.dynamicRenderer = fn;
  }

  setOverlayRenderer(fn: (v: RenderView) => void): void {
    this.overlayRenderer = fn;
  }

  setEditing(ids: Iterable<string>): void {
    this.editingIds = new Set(ids);
    // bg too: images render there and must vanish while their ghost drags (RND-1)
    this.scheduler.invalidate("bg", "static", "dynamic");
  }

  private createCanvases(): void {
    for (const id of RENDER_LAYERS) {
      const canvas = document.createElement("canvas");
      canvas.dataset.layer = id;
      canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;";
      this.container.appendChild(canvas);
      this.scheduler.attach(id, canvas, (ctx, w, h, dpr) => this.renderLayer(id, ctx, w, h, dpr));
      this.topCanvas = canvas;
    }
    for (const canvas of this.container.querySelectorAll("canvas")) {
      (canvas as HTMLCanvasElement).style.pointerEvents = canvas === this.topCanvas ? "auto" : "none";
    }
    this.topCanvas.tabIndex = 0; // canvas keeps keyboard focus (TL-2)
    this.topCanvas.style.outline = "none";
    this.topCanvas.style.cursor = this.baseCursor;
  }

  /** Tool-driven cursor; pan grab/grabbing layers over it and restores it on pan end. */
  setCursor(cursor: string | null): void {
    this.baseCursor = cursor ?? "crosshair";
    if (this.panPointer === null && !this.spaceHeld) this.topCanvas.style.cursor = this.baseCursor;
  }

  private view(ctx: CanvasRenderingContext2D, cssW: number, cssH: number, dpr: number): RenderView {
    return { ctx, cam: this.camera.current, cssW, cssH, dpr };
  }

  /** RND-4 blit path: true when the cached render was reused for this frame. */
  private tryBlit(id: "bg" | "static", ctx: CanvasRenderingContext2D, dpr: number): boolean {
    const snap = this.blitCache;
    if (!snap || snap.dpr !== dpr) return false;
    const img = snap.layers[id];
    if (!img) return false;
    const cam = this.camera.current;
    const k = cam.scale / snap.cam.scale;
    const vx = this.scheduler.width / 2;
    const vy = this.scheduler.height / 2;
    const dx = -vx * k + (snap.cam.cx - cam.cx) * cam.scale + vx;
    const dy = -vy * k + (snap.cam.cy - cam.cy) * cam.scale + vy;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(img, dx * dpr, dy * dpr, img.width * k, img.height * k);
    return true;
  }

  private renderLayer(
    id: RenderLayerId,
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    dpr: number
  ): void {
    switch (id) {
      case "bg":
        if (this.tryBlit("bg", ctx, dpr)) return;
        renderBg(ctx, w, h, dpr, this.camera.current, readTheme(), this.store.doc.canvasGrid.visible);
        renderImages(this.view(ctx, w, h, dpr), this.store.doc, this.assets, this.editingIds, this.dimImages);
        this.renderedCam = { ...this.camera.current };
        break;
      case "static":
        if (this.tryBlit("static", ctx, dpr)) return;
        renderDocEntities(this.view(ctx, w, h, dpr), this.store.doc, this.editingIds);
        this.renderedCam = { ...this.camera.current };
        break;
      case "dynamic":
        this.dynamicRenderer?.(this.view(ctx, w, h, dpr));
        break;
      case "overlay":
        this.overlayRenderer?.(this.view(ctx, w, h, dpr));
        break;
    }
  }

  /**
   * Keep the scheduler's believed size in lock-step with the real container. Input math
   * and rendering both derive from scheduler dims, so ANY drift (missed ResizeObserver,
   * display-scale change while minimised, …) offsets the whole canvas — self-heal instead.
   */
  syncSize(): boolean {
    const r = this.container.getBoundingClientRect();
    const w = Math.round(r.width);
    const h = Math.round(r.height);
    const dpr = window.devicePixelRatio || 1;
    if (w === this.scheduler.width && h === this.scheduler.height && dpr === this.scheduler.devicePixelRatio) {
      return false;
    }
    this.blitCache = null; // resized → cached render no longer valid (RND-4)
    this.renderedCam = null;
    this.scheduler.resize(w, h, dpr);
    return true;
  }

  private wireResize(): void {
    const ro = new ResizeObserver(() => this.syncSize());
    ro.observe(this.container);
    this.detachFns.push(() => ro.disconnect());

    // window resize as belt-and-braces alongside the ResizeObserver
    const onWinResize = () => this.syncSize();
    window.addEventListener("resize", onWinResize);
    this.detachFns.push(() => window.removeEventListener("resize", onWinResize));

    // EC-ARC-5: DPR changes — re-arm after every firing (a one-shot query goes stale after
    // the second zoom/monitor change: its match state stops toggling)
    let dprCleanup: (() => void) | null = null;
    const armDpr = (): void => {
      const mq = matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      const onChange = (): void => {
        mq.removeEventListener("change", onChange);
        this.syncSize();
        armDpr();
      };
      mq.addEventListener("change", onChange);
      dprCleanup = () => mq.removeEventListener("change", onChange);
    };
    armDpr();
    this.detachFns.push(() => dprCleanup?.());

    const onVis = () => {
      if (!document.hidden && !this.syncSize()) this.scheduler.invalidateAll();
    };
    document.addEventListener("visibilitychange", onVis);
    this.detachFns.push(() => document.removeEventListener("visibilitychange", onVis));
  }

  private setCamera(cam: Camera): void {
    // RND-4: snapshot the current full render once per gesture, blit until settle
    if (!this.blitCache && this.renderedCam) {
      const layers: Partial<Record<"bg" | "static", HTMLCanvasElement>> = {};
      for (const id of ["bg", "static"] as const) {
        const src = this.container.querySelector<HTMLCanvasElement>(`canvas[data-layer="${id}"]`);
        if (src && src.width > 0) {
          const copy = document.createElement("canvas");
          copy.width = src.width;
          copy.height = src.height;
          copy.getContext("2d")?.drawImage(src, 0, 0);
          layers[id] = copy;
        }
      }
      if (layers.bg || layers.static) {
        this.blitCache = { layers, cam: { ...this.renderedCam }, dpr: this.scheduler.devicePixelRatio };
      }
    }
    this.camera.current = cam;
    this.scheduler.invalidateAll();
    this.events.onCamera?.(cam, zoomPercent(cam));
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      this.settleTimer = null;
      this.blitCache = null;
      this.scheduler.invalidate("bg", "static"); // crisp re-render after the gesture (RND-4)
    }, 120);
  }

  private toLocal(e: PointerEvent | WheelEvent | MouseEvent): Vec {
    const r = this.container.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private mods(e: PointerEvent | KeyboardEvent | MouseEvent): PointerMods {
    return { shift: e.shiftKey, ctrl: e.ctrlKey, alt: e.altKey };
  }

  cursorWorld(local: Vec): Vec {
    return screenToWorld(this.camera.current, this.scheduler.width, this.scheduler.height, local);
  }

  /** Bring a box into view: nothing moves when it is already on screen. */
  showBounds(b: { minX: number; minY: number; maxX: number; maxY: number }): void {
    const W = this.scheduler.width;
    const H = this.scheduler.height;
    const cam = this.camera.current;
    const toScreen = (x: number, y: number) => ({ x: (x - cam.cx) * cam.scale + W / 2, y: (y - cam.cy) * cam.scale + H / 2 });
    const a = toScreen(b.minX, b.minY);
    const c = toScreen(b.maxX, b.maxY);
    if (a.x >= 0 && a.y >= 0 && c.x <= W && c.y <= H) return;
    // Room above it: the number panel sits over the top-left of the drawing.
    this.setCamera(fitBounds(W, H, { ...b, minY: b.minY - (b.maxY - b.minY) * 0.3 }));
  }

  zoomExtents(): void {
    this.setCamera(fitBounds(this.scheduler.width, this.scheduler.height, docBounds(this.store.doc)));
  }

  zoomBy(factor: number): void {
    const center = { x: this.scheduler.width / 2, y: this.scheduler.height / 2 };
    this.setCamera(zoomAt(this.camera.current, this.scheduler.width, this.scheduler.height, center, factor));
  }

  /** Screen-space pan (EC-TL-18 autoscroll and trackpad). */
  panScreen(dx: number, dy: number): void {
    this.setCamera(panBy(this.camera.current, dx, dy));
  }

  private wireInput(): void {
    const canvas = this.topCanvas;

    canvas.addEventListener("pointerdown", (e) => {
      canvas.focus();
      this.syncSize(); // self-heal any size drift BEFORE coordinates are interpreted
      const isPanButton = e.button === 1 || (e.button === 0 && this.spaceHeld);
      try {
        canvas.setPointerCapture(e.pointerId); // EC-TL-12
      } catch {
        // inactive pointer (synthetic events, some pen edge cases) — capture is best-effort
      }
      if (isPanButton) {
        this.panPointer = e.pointerId;
        this.lastPointer = this.toLocal(e);
        this.pannedDuringSpace = true;
        canvas.style.cursor = "grabbing";
        e.preventDefault();
        return;
      }
      this.delegate?.pointerDown(this.toLocal(e), this.mods(e), e.button);
      e.preventDefault();
    });

    canvas.addEventListener("pointermove", (e) => {
      const local = this.toLocal(e);
      if (this.panPointer === e.pointerId) {
        this.setCamera(panBy(this.camera.current, local.x - this.lastPointer.x, local.y - this.lastPointer.y));
        this.lastPointer = local;
      } else {
        this.delegate?.pointerMove(local, this.mods(e));
      }
      this.events.onCursor?.(this.cursorWorld(local));
    });

    const endPan = (e: PointerEvent) => {
      if (this.panPointer === e.pointerId) {
        this.panPointer = null;
        canvas.style.cursor = this.spaceHeld ? "grab" : this.baseCursor;
        return true;
      }
      return false;
    };
    canvas.addEventListener("pointerup", (e) => {
      if (!endPan(e)) this.delegate?.pointerUp(this.toLocal(e), this.mods(e), e.button);
    });
    canvas.addEventListener("pointercancel", endPan);

    canvas.addEventListener("dblclick", (e) => {
      this.delegate?.doubleClick(this.toLocal(e), this.mods(e));
    });

    canvas.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        this.syncSize();
        const local = this.toLocal(e);
        if (this.delegate?.wheel?.(local, e.deltaY, this.mods(e))) return;
        // Mouse-vs-trackpad heuristic — see DECISIONS.md [04 §RND-2]
        const isZoom =
          e.ctrlKey || e.deltaMode !== 0 || (Math.abs(e.deltaY) >= 50 && Math.abs(e.deltaX) < 1);
        if (isZoom) {
          const factor = Math.pow(1.1, -e.deltaY / 100);
          this.setCamera(
            zoomAt(this.camera.current, this.scheduler.width, this.scheduler.height, local, factor)
          );
        } else {
          this.setCamera(panBy(this.camera.current, -e.deltaX, -e.deltaY));
        }
      },
      { passive: false }
    );

    canvas.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.delegate?.contextMenu?.(this.toLocal(e), this.mods(e));
    });

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField =
        target && typeof target.closest === "function"
          ? target.closest("input, textarea, [contenteditable], [data-own-keys]")
          : null;
      if (inField) return; // TL-2: shortcuts suppressed while editing text (and in a [data-own-keys] block)
      // TL-5a held interaction modifiers (E = slide edge ⟂, W = move edge, X = extrude new segments)
      if (!e.ctrlKey && !e.altKey && (e.key === "e" || e.key === "w" || e.key === "x")) {
        this.heldKeys.add(e.key);
      }
      if (e.code === "Space" && !e.repeat) {
        this.spaceHeld = true;
        this.spaceDownAt = performance.now();
        this.pannedDuringSpace = false;
        if (this.panPointer === null) this.topCanvas.style.cursor = "grab";
        e.preventDefault();
        return;
      }
      if (e.ctrlKey && e.key === "0") {
        this.zoomExtents();
        e.preventDefault();
        return;
      }
      if (e.ctrlKey && (e.key === "+" || e.key === "=")) {
        this.zoomBy(1.25);
        e.preventDefault();
        return;
      }
      if (e.ctrlKey && e.key === "-") {
        this.zoomBy(1 / 1.25);
        e.preventDefault();
        return;
      }
      if (e.ctrlKey && !e.shiftKey && e.key.toLowerCase() === "z") {
        this.store.undo();
        e.preventDefault();
        return;
      }
      if (e.ctrlKey && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) {
        this.store.redo();
        e.preventDefault();
        return;
      }
      if (
        this.delegate?.key({ key: e.key, ctrl: e.ctrlKey, shift: e.shiftKey, alt: e.altKey })
      ) {
        e.preventDefault();
      } else if (e.key === "Alt" || e.key === "Tab") {
        e.preventDefault(); // Alt = snap suspend, Tab = cycle — never browser focus/menu
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      this.heldKeys.delete(e.key.toLowerCase());
      if (e.code === "Space") {
        const wasQuickTap =
          !this.pannedDuringSpace && performance.now() - this.spaceDownAt < 200 && this.panPointer === null;
        this.spaceHeld = false;
        if (this.panPointer === null) this.topCanvas.style.cursor = this.baseCursor;
        if (wasQuickTap) this.delegate?.spaceTap?.();
      }
    };
    const onBlur = () => this.heldKeys.clear(); // missed keyups when the window loses focus
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    this.detachFns.push(() => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    });
  }

  destroy(): void {
    for (const fn of this.detachFns) fn();
    this.container.replaceChildren();
  }
}
